import axios from 'axios';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useState } from 'react';
import { toast } from 'sonner';
import { resizeImage } from '@/app/document/_helpers';
import { localSwrConfig } from '@/helpers/swr';
import { formatAndThrowApiError } from '@/helpers/api';

const useActivateUser = () => {
  const activateUser = async (uid, payload) => {
    try {
      const url = `/user/activate/${uid}`;
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      formatAndThrowApiError(error);
    }
  };

  return activateUser;
};

const useGetUserToActivate = (uid) => {
  const urlStr = `/user/activate/${uid}`;
  const swr = useSWRImmutable(
    urlStr,
    async (url) => {
      const result = await axios.get(url);
      return result.data;
    },
    { shouldRetryOnError: false }
  );

  return swr;
};

// A rejected session is handled by UserAuthentication, the one gate every consumer of this
// hook renders under - not here. Logging out from the fetcher fired once per consumer, left
// the thrown error to render the error page anyway, and read `err.response.status` on an
// error that has no response when the API is unreachable.
const useSelf = (shouldRevalidate = true) => {
  const urlStr = `/self`;

  const swr = useSWR(
    urlStr,
    async (url) => {
      try {
        const result = await axios.get(url);
        return result.data;
      } catch (err) {
        formatAndThrowApiError(err);
      }
    },
    shouldRevalidate ? undefined : localSwrConfig
  );

  return swr;
};

const usePutSelf = () => {
  const putSelf = async (payload) => {
    try {
      const url = `/self`;
      const response = await axios.put(url, payload);
      return response.data;
    } catch (error) {
      toast.error('An error occurred updating user');
      formatAndThrowApiError(error);
    }
  };

  return putSelf;
};

const usePutPassword = () => {
  const putPassword = async (payload) => {
    try {
      const url = `/self/change-password`;
      const response = await axios.put(url, payload);
      toast.success('Password updated successfully');
      return response.data;
    } catch (error) {
      toast.error('An error occurred updating password');
      formatAndThrowApiError(error);
    }
  };

  return putPassword;
};

const useUploadUserAvatar = () => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState();
  const putSelf = usePutSelf();

  const uploadUserAvatar = async (file, onUploadSuccess) => {
    try {
      const resizedFile = await resizeImage(file, 600, 600);

      // Get the signed url from our server
      const signature = await axios.get(`/self/avatar/upload-signature?fileType=${resizedFile.type}&fileName=${resizedFile.name}`);

      // Upload the file to the signed google url
      await axios.put(signature.data.signedUrl, resizedFile, {
        headers: {
          'Content-Type': resizedFile.type
        },
        withCredentials: false,
        onUploadProgress: (progressEvent) => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
        onError: (error) => {
          setError(error.message || 'An error occurred during upload');
          throw error;
        }
      });
      const newUser = await putSelf({ avatar_url: signature.data.publicUrl });
      onUploadSuccess(newUser);
      setProgress(0);
      setError(null);
    } catch (error) {
      toast.error('An error occurred during upload');
      setError(error.message || 'An error occurred during upload');
      setProgress(0);
      throw error;
    }
  };

  return { progress, error, uploadUserAvatar };
};

const useIsUserSSO = () => {
  const self = useSelf(false);
  return self.data.login_type === 'sso';
};

export { useActivateUser, useGetUserToActivate, useSelf, usePutSelf, usePutPassword, useUploadUserAvatar, useIsUserSSO };
